import { parseEventDate, formatEventDate, formatEventTime } from '../utils/date-helpers.js';
import { renderPartyPanda } from '../utils/mascots.js';
import { escapeHtml } from '../utils/html-utils.js';

// Events List Component
class EventsList extends HTMLElement {
  constructor() {
    super();
    this.upcomingEvents = [];
    this.pastEvents = [];
    this.deletedEvents = [];
    this.loading = true;
    this.showDeleted = false; // Show deleted events toggle
    this.showPastOnly = false; // Show only past events toggle (no future)
  }

  async connectedCallback() {
    await this.loadEvents();
    this.render();
  }

  async loadEvents() {
    try {
      this.loading = true;

      // Load regular events with date range: 2 weeks future, 1 week past
      const now = new Date();
      const startDate = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000)); // 1 week ago
      const endDate = new Date(now.getTime() + (14 * 24 * 60 * 60 * 1000)); // 2 weeks from now

      const queryParams = new URLSearchParams({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      });

      const response = await window.API.request(`/events?${queryParams}`);
      this.upcomingEvents = response.upcoming_events || [];
      this.pastEvents = response.past_events || [];

      // Load deleted events (we'll load on demand)
      this.loading = false;
      this.render();
    } catch (error) {
      window.logger.error('Failed to load events:', error);
      this.loading = false;
      this.render();
    }
  }

  async loadDeletedEvents() {
    try {
      const response = await window.API.request('/events/deleted');
      this.deletedEvents = response.deleted_events || [];
      this.render();
    } catch (error) {
      window.logger.error('Failed to load deleted events:', error);
      this.deletedEvents = [];
    }
  }

  render() {
    if (this.loading) {
      this.innerHTML = `
        <div class="events-container">
          <div class="loading">Loading events...</div>
        </div>
      `;
      return;
    }

    this.innerHTML = `
      <div class="events-container">
        <div class="events-header">
          <h1>My Events</h1>
          <div class="header-actions">
            <a href="/events/calendar" class="btn-view-calendar">📅 Calendar View</a>
            <button class="btn-create-event" id="createEventBtn">
              🎉 Create Event
            </button>
          </div>
        </div>

        <toolbar-action-bar label="Filters">
          <div slot="actions" class="toggle-actions">
              <label class="toggle-checkbox-label icon-only" title="Show deleted events">
                <span class="toggle-icon">🗑️</span>
                <input type="checkbox" class="toggle-checkbox" id="showDeletedCheckbox" ${this.showDeleted ? 'checked' : ''}>
                <span class="toggle-checkbox-custom"></span>
              </label>
              <label class="toggle-checkbox-label icon-only" title="Show only past events">
                <span class="toggle-icon">📅</span>
                <input type="checkbox" class="toggle-checkbox" id="showPastCheckbox" ${this.showPastOnly ? 'checked' : ''}>
                <span class="toggle-checkbox-custom"></span>
              </label>
          </div>
        </toolbar-action-bar>


        ${this.renderEvents()}
      </div>
    `;

    this.attachEventListeners();
  }




  renderEmpty() {
    return `
      <div class="empty-state">
        <div class="party-panda">
          ${renderPartyPanda()}
        </div>
        <h2>Hi there! I'm Party Panda! 🎊</h2>
        <p class="panda-message">
          I'm SO excited to help you create your first event! 
          Whether it's game night, a backyard BBQ, or a surprise party, 
          I'll be here to make sure it's absolutely amazing!
        </p>
        <p class="panda-encouragement">
          Trust me, your friends are going to LOVE what we create together! 
          Let's get this party started! 🥳
        </p>
        <button class="btn-create-event" id="createEventBtnEmpty">
          ✨ Create My First Event ✨
        </button>
      </div>
    `;
  }

  renderEvents() {
    const now = new Date();

    // Determine what to show based on toggle combinations
    let eventsToShow = [];
    const showDeleted = this.showDeleted;
    const showPastOnly = this.showPastOnly;

    if (!showDeleted && !showPastOnly) {
      // Default: Show future events + past from last week
      eventsToShow = [...this.upcomingEvents, ...this.pastEvents];
    } else if (!showDeleted && showPastOnly) {
      // Show only past events (no future)
      eventsToShow = this.pastEvents;
    } else if (showDeleted && !showPastOnly) {
      // Show only deleted events (no past/current)
      const deletedFuture = this.deletedEvents.filter(e => new Date(e.start_time) >= now);
      const deletedPast = this.deletedEvents.filter(e => new Date(e.start_time) < now);

      if (deletedFuture.length === 0 && deletedPast.length === 0) {
        return this.renderEmpty();
      }

      if (deletedFuture.length > 0 && deletedPast.length > 0) {
        return `
          <div class="events-list">
            <div class="events-section deleted-future">
              <h2 class="events-section-title">Deleted Future Events</h2>
              ${deletedFuture.map(event => this.renderDeletedEventCard(event)).join('')}
            </div>
            <hr class="events-divider">
            <div class="events-section deleted-past">
              <h2 class="events-section-title">Deleted Past Events</h2>
              ${deletedPast.map(event => this.renderDeletedEventCard(event)).join('')}
            </div>
          </div>
        `;
      } else if (deletedFuture.length > 0) {
        return `
          <div class="events-list">
            <div class="events-section deleted-future">
              <h2 class="events-section-title">Deleted Future Events</h2>
              ${deletedFuture.map(event => this.renderDeletedEventCard(event)).join('')}
            </div>
          </div>
        `;
      } else {
        return `
          <div class="events-list">
            <div class="events-section deleted-past">
              <h2 class="events-section-title">Deleted Past Events</h2>
              ${deletedPast.map(event => this.renderDeletedEventCard(event)).join('')}
            </div>
          </div>
        `;
      }
    } else if (showDeleted && showPastOnly) {
      // Show all deleted events
      eventsToShow = this.deletedEvents;
      if (eventsToShow.length === 0) {
        return this.renderEmpty();
      }
      return `
        <div class="events-list">
          <div class="events-section deleted-events">
            ${eventsToShow.map(event => this.renderDeletedEventCard(event)).join('')}
          </div>
        </div>
      `;
    }

    if (eventsToShow.length === 0) {
      return this.renderEmpty();
    }

    // Render non-deleted events with sections
    const upcomingEvents = eventsToShow.filter(e => new Date(e.start_time) >= now);
    const pastEvents = eventsToShow.filter(e => new Date(e.start_time) < now);

    return `
      <div class="events-list">
        ${upcomingEvents.length > 0 ? `
          <div class="events-section upcoming-events">
            ${upcomingEvents.map(event => this.renderEventCard(event)).join('')}
          </div>
        ` : ''}
        ${upcomingEvents.length > 0 && pastEvents.length > 0 ? '<hr class="events-divider">' : ''}
        ${pastEvents.length > 0 ? `
          <div class="events-section past-events">
            <h2 class="events-section-title">Past Events</h2>
            ${pastEvents.map(event => this.renderEventCard(event)).join('')}
          </div>
        ` : ''}
      </div>
    `;
  }

  renderDeletedEvents() {
    if (this.deletedEvents.length === 0) {
      return `
        <div class="empty-state">
          <div class="party-panda">
            ${renderPartyPanda()}
          </div>
          <h2>Nothing here! 🎉</h2>
          <p class="panda-message">
            No deleted events found. Party Panda is celebrating because everything is organized!
          </p>
        </div>
      `;
    }

    return `
      <div class="events-grid">
        ${this.deletedEvents.map(event => this.renderDeletedEventCard(event)).join('')}
      </div>
    `;
  }

  renderDeletedEventCard(event) {
    const eventTimezone = event.timezone || 'UTC';
    const startDate = parseEventDate(event.start_time, eventTimezone);
    const dateStr = formatEventDate(startDate, {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
    const timeStr = formatEventTime(startDate);

    const deletedDate = event.deleted_at ? parseEventDate(event.deleted_at, eventTimezone) : null;
    const deletedDateStr = deletedDate ? formatEventDate(deletedDate, {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }) : '';

    return `
      <div class="event-card deleted" data-event-id="${event.id}">
        <div class="event-card-header">
          <h3>${escapeHtml(event.title)}</h3>
          <span class="badge-deleted">🗑️ Deleted</span>
        </div>
        <div class="event-card-body">
          <div class="event-date">
            <strong>${dateStr}</strong> at ${timeStr}
          </div>
          <div class="deleted-info">
            <p class="deleted-date">Deleted on ${deletedDateStr}</p>
            ${event.deletion_reason ? `
              <p class="deletion-reason">Reason: ${escapeHtml(event.deletion_reason)}</p>
            ` : ''}
            ${event.attendee_count > 0 ? `
              <p class="attendee-count">Had ${event.attendee_count} attendee${event.attendee_count > 1 ? 's' : ''}</p>
            ` : ''}
          </div>
        </div>
        <div class="event-card-footer">
          ${event.can_restore ? `
            <button class="btn-restore" data-event-id="${event.id}">🔄 Restore Event</button>
          ` : '<span class="restore-disabled">Already restored</span>'}
        </div>
      </div>
    `;
  }

  renderEventCard(event) {
    const eventTimezone = event.timezone || 'UTC';
    // Parse event time and convert to user's local timezone
    const startDate = parseEventDate(event.start_time, eventTimezone);
    const dateStr = formatEventDate(startDate, {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
    const timeStr = formatEventTime(startDate);

    const rsvpBadge = event.user_rsvp ? `
      <span class="rsvp-badge rsvp-${event.user_rsvp}">
        ${event.user_rsvp === 'yes' ? '✓ Going' : event.user_rsvp === 'maybe' ? '? Maybe' : '✗ Not Going'}
      </span>
    ` : '';

    // Use the instance's own ID
    const eventId = event.id;

    return `
      <div class="event-card${event.is_cancelled ? ' cancelled' : ''}" data-event-id="${event.id}">
        <div class="event-card-header">
          <h3><a href="/events/${eventId}" class="event-title-link">${event.is_cancelled ? '<span class="cancelled-label">Cancelled</span> ' : ''}${escapeHtml(event.title)}</a></h3>
          ${event.is_public ? '<span class="badge-public">🌍 Public</span>' : '<span class="badge-private">🔒 Private</span>'}
        </div>
        <div class="event-card-body">
          <div class="event-date">
            <strong>${dateStr}</strong> at ${timeStr}
          </div>
          ${event.location ? `
            <div class="event-location">
              📍 ${escapeHtml(event.location)}
            </div>
          ` : ''}
          ${event.yes_count > 0 ? `
            <div class="event-attendees">
              👥 ${event.yes_count} attending
              ${event.maybe_count > 0 ? `, ${event.maybe_count} maybe` : ''}
            </div>
          ` : ''}
          ${rsvpBadge}
        </div>
        <div class="event-card-footer">
          <button class="btn-view" data-event-id="${eventId}">View Details</button>
        </div>
      </div>
    `;
  }


  attachEventListeners() {
    // Create event buttons
    const createBtns = this.querySelectorAll('#createEventBtn, #createEventBtnEmpty');
    createBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        window.router.navigate('/events/new');
      });
    });

    // Checkbox toggle for deleted events
    const deletedCheckbox = this.querySelector('#showDeletedCheckbox');
    if (deletedCheckbox) {
      deletedCheckbox.addEventListener('change', async (e) => {
        this.showDeleted = e.target.checked;

        // Load deleted events if showing them for the first time
        if (this.showDeleted && this.deletedEvents.length === 0) {
          await this.loadDeletedEvents();
        }
        this.render();
      });
    }

    // Checkbox toggle for past events only
    const pastCheckbox = this.querySelector('#showPastCheckbox');
    if (pastCheckbox) {
      pastCheckbox.addEventListener('change', (e) => {
        this.showPastOnly = e.target.checked;
        this.render();
      });
    }

    // View event buttons
    this.querySelectorAll('.btn-view').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation(); // Prevent event bubbling
        const eventId = e.currentTarget.dataset.eventId;
        if (eventId) {
          window.router.navigate(`/events/${eventId}`);
        } else {
          window.logger.error('No eventId found on button', e.currentTarget);
        }
      });
    });

    // Event title links
    this.querySelectorAll('.event-title-link').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const href = link.getAttribute('href');
        if (href && window.router) {
          window.router.navigate(href);
        } else if (href) {
          window.location.href = href;
        }
      });
    });

    // Restore event buttons
    this.querySelectorAll('.btn-restore').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const eventId = e.target.dataset.eventId;
        const confirmed = confirm('Are you sure you want to restore this event?');
        if (confirmed) {
          await this.restoreEvent(eventId);
        }
      });
    });
  }

  async restoreEvent(eventId) {
    try {
      // Find the event to check attendee count
      const event = this.deletedEvents.find(e => e.id === parseInt(eventId));

      // Get restore reason, required if there were attendees
      let restoreReason = 'No attendees.';
      if (event && event.attendee_count > 0) {
        restoreReason = prompt(
          'Please provide a reason for restoring this event (e.g., "Event rescheduled", "New venue secured"):\n\n' +
          'This will help attendees understand why the event was restored.'
        );

        if (restoreReason === null) {
          // User cancelled
          return;
        }

        if (!restoreReason.trim()) {
          alert('Please provide a reason for restoration.');
          return;
        }
      }

      const response = await window.API.request(`/events/${eventId}/restore`, {
        method: 'POST',
        body: JSON.stringify({
          restore_reason: restoreReason
        })
      });

      if (response.message) {
        alert('Event restored successfully!');

        // Remove from deleted list and reload active events
        this.deletedEvents = this.deletedEvents.filter(e => e.id !== parseInt(eventId));
        await this.loadEvents();

        // Switch back to active events view
        this.showDeleted = false;
        this.render();
      }
    } catch (error) {
      window.logger.error('Failed to restore event:', error);
      alert('Failed to restore event. Please try again.');
    }
  }
}

customElements.define('events-list', EventsList);

